/**
 * Admin Service
 * Handles all admin-related API calls
 */

import api from './api';

const adminService = {
  /**
   * Get dashboard statistics
   */
  getDashboardStats: async () => {
    try {
      const response = await api.get('/admin/dashboard');
      return response?.data ?? response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get revenue analytics
   */
  getRevenueAnalytics: async (filters = {}) => {
    try {
      const queryString = api.buildQueryString(filters);
      const response = await api.get(`/admin/analytics/revenue${queryString}`);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get bookings analytics
   */
  getBookingsAnalytics: async (filters = {}) => {
    try {
      const queryString = api.buildQueryString(filters);
      const response = await api.get(`/admin/analytics/bookings${queryString}`);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get popular trips
   */
  getPopularTrips: async (filters = {}) => {
    try {
      const queryString = api.buildQueryString(filters);
      const response = await api.get(`/admin/analytics/popular_trips${queryString}`);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get all users
   */
  getAllUsers: async (filters = {}) => {
    try {
      const queryString = api.buildQueryString(filters);
      const response = await api.get(`/admin/users${queryString}`);
      const data = response?.data ?? response;
      return data?.users ? { users: data.users, pagination: response?.pagination } : data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Create user (admin/super_admin only)
   */
  createUser: async (userData) => {
    try {
      const response = await api.post('/admin/users', {
        user: {
          email: userData.email,
          password: userData.password,
          first_name: userData.firstName ?? userData.first_name,
          last_name: userData.lastName ?? userData.last_name,
          phone: userData.phone,
          city: userData.city,
          role: userData.role ?? 'customer',
        },
      });
      const data = response?.data ?? response;
      return data?.user ? { user: data.user } : data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get user by ID
   */
  getUserById: async (id) => {
    try {
      const response = await api.get(`/admin/users/${id}`);
      const data = response?.data ?? response;
      return data?.user ? { user: data.user } : data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update user
   */
  updateUser: async (id, userData) => {
    try {
      const response = await api.put(`/admin/users/${id}`, {
        first_name: userData.firstName ?? userData.first_name,
        last_name: userData.lastName ?? userData.last_name,
        phone: userData.phone,
        city: userData.city,
        role: userData.role,
        is_active: userData.isActive ?? userData.is_active,
        is_verified: userData.isVerified ?? userData.is_verified,
      });
      const data = response?.data ?? response;
      return data?.user ? { user: data.user } : data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Delete user
   */
  deleteUser: async (id) => {
    try {
      const response = await api.delete(`/admin/users/${id}`);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Refund a payment (admin only). Optional partial amount; defaults to full.
   */
  refundPayment: async (id, amount) => {
    try {
      const body = amount != null && amount !== '' ? { amount } : {};
      const response = await api.post(`/payments/${id}/refund`, body);
      const data = response?.data ?? response;
      return { payment: data?.payment ?? data, message: response?.message };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get all payments (Admin only)
   */
  getAllPayments: async (filters = {}) => {
    try {
      const queryString = api.buildQueryString(filters);
      const response = await api.get(`/admin/payments${queryString}`);
      const data = response?.data ?? response;
      return data?.payments ? { payments: data.payments, pagination: response?.pagination } : data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get activity logs
   */
  getActivityLogs: async (filters = {}) => {
    try {
      const queryString = api.buildQueryString(filters);
      const response = await api.get(`/admin/logs${queryString}`);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get plan requests (booking enquiries from finalized plans)
   * @param {Object} filters - { status, page, per_page }
   */
  getPlanRequests: async (filters = {}) => {
    try {
      const queryString = api.buildQueryString(filters);
      const response = await api.get(`/admin/plan_requests${queryString}`);
      const data = response?.data ?? response;
      return {
        planRequests: data?.plan_requests ?? [],
        pagination: response?.pagination ?? data?.pagination ?? {},
      };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update a plan request's status (pending → contacted → confirmed → closed)
   */
  updatePlanRequest: async (id, status) => {
    try {
      const response = await api.patch(`/admin/plan_requests/${id}`, { plan_request: { status } });
      const data = response?.data ?? response;
      return { planRequest: data?.plan_request ?? data };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Approve a plan request and book it on the customer's behalf.
   * @param {string} id
   * @param {Object} details - { travel_date, num_travelers, total_price }
   */
  bookPlanRequest: async (id, details = {}) => {
    try {
      const response = await api.post(`/admin/plan_requests/${id}/book`, details);
      const data = response?.data ?? response;
      return { booking: data?.booking ?? data, message: response?.message };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Global cross-entity admin search (powers the command palette).
   */
  search: async (q) => {
    try {
      const response = await api.get(`/admin/search?q=${encodeURIComponent(q)}`);
      const data = response?.data ?? response;
      return { results: data?.results ?? [], count: data?.count ?? 0 };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Impersonate a user ("view as"). Super-admin only. Returns a short-lived token.
   */
  impersonate: async (userId) => {
    try {
      const response = await api.post(`/admin/users/${userId}/impersonate`);
      const data = response?.data ?? response;
      return { token: data?.token, user: data?.user, message: response?.message };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Download a CSV export for a resource. Triggers a browser download.
   * @param {string} resource - bookings|users|payments|contacts|subscribers
   * @param {Object} filters
   */
  exportCsv: async (resource, filters = {}) => {
    const qs = api.buildQueryString(filters);
    const url = `${api.API_BASE_URL}/admin/exports/${resource}${qs}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${api.getAuthToken()}` } });
    if (!res.ok) throw new Error(`Export failed (${res.status})`);
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = `${resource}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
    return { success: true };
  },

  /**
   * Run a bulk action on many records.
   * @param {string} resource - reviews|contacts|bookings|subscribers
   * @param {Object} payload - { ids: [], action: '', value }
   */
  bulkAction: async (resource, payload) => {
    try {
      const response = await api.post(`/admin/bulk/${resource}`, payload);
      const data = response?.data ?? response;
      return { affected: data?.affected ?? 0, message: response?.message };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Send a broadcast email. Super-admin only.
   * @param {Object} payload - { subject, body, audience }
   */
  broadcast: async (payload) => {
    try {
      const response = await api.post('/admin/broadcast', payload);
      const data = response?.data ?? response;
      return { queued: data?.queued ?? 0, message: response?.message };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get app settings + feature flags, grouped by category.
   */
  getSettings: async () => {
    try {
      const response = await api.get('/admin/settings');
      const data = response?.data ?? response;
      return { settings: data?.settings ?? {} };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update a single setting/flag by key. Super-admin only (server-enforced).
   */
  updateSetting: async (key, value) => {
    try {
      const response = await api.patch(`/admin/settings/${key}`, { value });
      const data = response?.data ?? response;
      return { setting: data?.setting ?? data };
    } catch (error) {
      throw error;
    }
  },

  /**
   * System health + live counts for the status panel.
   */
  getSystemHealth: async () => {
    try {
      const response = await api.get('/admin/system/health');
      return response?.data ?? response;
    } catch (error) {
      throw error;
    }
  },
};

export default adminService;
